// src/store/permissions.js
import { defineStore } from 'pinia';
import { apiClient } from '@/services/auth-header';

export const usePermissionsStore = defineStore('permissions', {
    state: () => ({
        userPermissions: null,
        menuPermissions: {},
        loading: false,
        error: null,
        lastFetched: null, // 마지막 갱신 시간
        refreshInterval: null, // 자동 갱신 인터벌
    }),
    
    actions: {
        async fetchUserPermissions(forceRefresh = false) {
            // 실시간 권한 업데이트를 위해 forceRefresh 옵션 추가
            if (this.userPermissions && !forceRefresh) {
                // 5분 이내 데이터는 재사용 (선택적 캐싱)
                const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
                if (this.lastFetched && this.lastFetched > fiveMinutesAgo) {
                    return this.userPermissions;
                }
            }
            
            this.loading = true;
            this.error = null;
            
            try {
                const response = await apiClient.get('/auth/me/permissions');
                this.userPermissions = response.data;
                this.lastFetched = Date.now();
                
                // 메뉴별 권한을 쉽게 접근할 수 있도록 맵으로 변환 (성능 최적화)
                this.menuPermissions = response.data.menuPermissions.reduce((acc, menu) => {
                    acc[menu.menuCode] = {
                        canRead: menu.canRead,
                        canWrite: menu.canWrite,
                        canDelete: menu.canDelete,
                        menuName: menu.menuName
                    };
                    return acc;
                }, {});
                
                return this.userPermissions;
            } catch (error) {
                this.error = error.response?.data?.message || '권한 정보를 불러오는데 실패했습니다.';
                throw error;
            } finally {
                this.loading = false;
            }
        },
        
        clearPermissions() {
            this.userPermissions = null;
            this.menuPermissions = {};
            this.error = null;
            this.lastFetched = null;
            this.stopAutoRefresh();
        },
        
        // 실시간 권한 업데이트를 위한 메서드
        async refreshPermissions() {
            return this.fetchUserPermissions(true);
        },
        
        // 자동 갱신 시작 (옵션)
        startAutoRefresh(intervalMs = 30000) { // 30초마다
            this.stopAutoRefresh();
            this.refreshInterval = setInterval(() => {
                this.refreshPermissions().catch(console.error);
            }, intervalMs);
        },
        
        // 자동 갱신 중지
        stopAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        
        hasMenuPermission(menuCode, action = 'READ') {
            // 빠른 실패 - 권한 정보가 로드되지 않은 경우
            if (!this.userPermissions || !this.menuPermissions) {
                return false;
            }
            
            const menu = this.menuPermissions[menuCode];
            if (!menu) return false;
            
            // 성능 최적화: switch 대신 객체 키 접근
            const actionKey = `can${action.charAt(0).toUpperCase() + action.slice(1).toLowerCase()}`;
            return Boolean(menu[actionKey]);
        },
        
        canAccessMenu(menuCode) {
            return this.hasMenuPermission(menuCode, 'READ');
        }
    },
    
    getters: {
        isLoaded: (state) => !!state.userPermissions,
        currentUser: (state) => state.userPermissions ? {
            accountId: state.userPermissions.accountId,
            name: state.userPermissions.name,
            email: state.userPermissions.email,
            roleName: state.userPermissions.roleName
        } : null,
    },
});